import { useHistory, useLocation } from 'react-router-dom';
import { useMemo } from 'react';

export type AdminListQuery = {
    q: string;
    type: string;
    finished: boolean;
    expired: boolean;
    page: number;
    pageSize: number;
};

const DEFAULTS: AdminListQuery = {
    q: '',
    type: '',
    finished: false,
    expired: true,
    page: 1,
    pageSize: 20,
};

function parseBool(val: string | null, fallback: boolean): boolean {
    if (val === null) return fallback;
    return val === '1' || val === 'true';
}

function parseInt10(val: string | null, fallback: number, min = 1): number {
    if (val === null) return fallback;
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < min) return fallback;
    return n;
}

export function useAdminListQuery() {
    const history = useHistory();
    const location = useLocation();

    const state = useMemo<AdminListQuery>(() => {
        const p = new URLSearchParams(location.search);
        return {
            q: p.get('q') ?? DEFAULTS.q,
            type: p.get('type') ?? DEFAULTS.type,
            finished: parseBool(p.get('finished'), DEFAULTS.finished),
            expired: parseBool(p.get('expired'), DEFAULTS.expired),
            page: parseInt10(p.get('page'), DEFAULTS.page),
            pageSize: parseInt10(p.get('pageSize'), DEFAULTS.pageSize),
        };
    }, [location.search]);

    const update = (patch: Partial<AdminListQuery>, opts?: { resetPage?: boolean }) => {
        const next = { ...state, ...patch };
        if (opts?.resetPage) next.page = 1;
        const p = new URLSearchParams();
        if (next.q) p.set('q', next.q);
        if (next.type) p.set('type', next.type);
        if (next.finished !== DEFAULTS.finished) p.set('finished', next.finished ? '1' : '0');
        if (next.expired !== DEFAULTS.expired) p.set('expired', next.expired ? '1' : '0');
        if (next.page !== DEFAULTS.page) p.set('page', String(next.page));
        if (next.pageSize !== DEFAULTS.pageSize) p.set('pageSize', String(next.pageSize));
        const qs = p.toString();
        history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' });
    };

    const reset = () => {
        history.replace({ pathname: location.pathname, search: '' });
    };

    const isDefault =
        state.q === DEFAULTS.q &&
        state.type === DEFAULTS.type &&
        state.finished === DEFAULTS.finished &&
        state.expired === DEFAULTS.expired &&
        state.page === DEFAULTS.page &&
        state.pageSize === DEFAULTS.pageSize;

    return { ...state, update, reset, isDefault };
}
